import { UI, HStack } from '@genial/components';
import { Typography } from '@genial/design-tokens';
import { EquityEnumsSubscriptionPeriodEnum } from '@genial/services/dist/models';
import { useNavigation } from '@react-navigation/native';
import { Box, Button } from 'native-base';
import React, { useCallback } from 'react';
import { useRecoilState } from 'recoil';

import { FilterModal } from '../../../../components/FilterModal';
import { getEmptySelections } from '../../../../components/FilterModal/constants';
import { DateRangeTypes } from '../../../../components/FilterModal/filters/DateRange';
import { filterHasSelection } from '../../../../components/FilterModal/handlers/filterHasSelection';
import { SignedInNavigationScreenNavigationProp } from '../../../../routes/types';
import { subscriptionsFiltersAtom } from '../../../../store/subscription/atoms';
import {
  defaultSubscriptionFilterType,
  otherSubscriptionFilterType,
  subscriptionDateRangeFieldName,
  subscriptionFilterTypeFieldName,
  subscriptionRadioItemsSelect,
} from '../../../../store/subscription/constants';
import { SelectPeriod } from '../../../CustomerDetail/components/Transactions/components/SelectPeriod/Index';

export interface SubscriptionFiltersProps {
  isMobile?: boolean;
}

export const SubscriptionFilters: React.FC<SubscriptionFiltersProps> = ({ isMobile }) => {
  const [{ appliedFilters, availableFilters }, setFiltersState] =
    useRecoilState(subscriptionsFiltersAtom);
  const { navigate } = useNavigation<SignedInNavigationScreenNavigationProp>();

  const filterSelectionSwitch = ({ filter }, fIndex: number) =>
    filterHasSelection(availableFilters[fIndex].type, filter, availableFilters[fIndex]);

  const onChangeFilterType = (value: EquityEnumsSubscriptionPeriodEnum) => {
    const emptySelected = getEmptySelections(availableFilters);

    const newSelectedFilters = emptySelected.map((filter) =>
      filter.fieldName === subscriptionFilterTypeFieldName ? { ...filter, filter: [value] } : filter
    );

    setFiltersState((prevState) => ({
      ...prevState,
      selectedFilters: newSelectedFilters,
      appliedFilters: newSelectedFilters.filter(filterSelectionSwitch),
    }));
  };

  const onChangeDates = (initialDate: Date, finalDate: Date) => {
    const emptySelected = getEmptySelections(availableFilters);

    const newSelectedFilters = emptySelected
      .map((filter) =>
        filter.fieldName === subscriptionDateRangeFieldName
          ? { ...filter, filter: { initialDate, finalDate } }
          : filter
      )
      .map((filter) =>
        filter.fieldName === subscriptionFilterTypeFieldName
          ? { ...filter, filter: [otherSubscriptionFilterType] }
          : filter
      );

    setFiltersState((prevState) => ({
      ...prevState,
      selectedFilters: newSelectedFilters,
      appliedFilters: newSelectedFilters.filter(filterSelectionSwitch),
    }));
  };

  const selectedFilterType =
    appliedFilters.find((filter) => filter.fieldName === subscriptionFilterTypeFieldName)
      ?.filter?.[0] || defaultSubscriptionFilterType;

  const defaultDateRange = {
    initialDate: new Date(new Date().setDate(new Date().getDate() - 30)),
    finalDate: new Date(),
  };

  const selectedDates =
    (appliedFilters.find((filter) => filter.fieldName === subscriptionDateRangeFieldName)
      ?.filter as DateRangeTypes.SelectionProps) || defaultDateRange;

  if (isMobile) {
    return (
      <HStack>
        <Button
          onPress={() => navigate('SubscriptionFiltersModal')}
          paddingX="inline.xxxs"
          marginY="stack.nano"
          variant="secondary"
          {...(!appliedFilters.length ? { rightIcon: <UI.Icon name="Funnel" size="xxxs" /> } : {})}>
          <HStack alignItems="center" justifyContent="center" space="inline.nano">
            <UI.Text variant="semibold" fontSize={Typography['fontsize.md']}>
              Filtrar
            </UI.Text>
            {!!appliedFilters.length && (
              <Box
                rounded="full"
                w="20px"
                h="20px"
                alignItems="center"
                justifyContent="center"
                bg={'brand.tertiary.light'}>
                <UI.Text
                  variant="regular"
                  fontSize={Typography['fontsize.xs']}
                  color="neutral.white">
                  {appliedFilters.length}
                </UI.Text>
              </Box>
            )}
          </HStack>
        </Button>
      </HStack>
    );
  }

  return (
    <SelectPeriod
      endDate={selectedDates.finalDate || defaultDateRange.finalDate}
      startDate={selectedDates.initialDate || defaultDateRange.initialDate}
      onSelectDates={onChangeDates}
      title={subscriptionRadioItemsSelect.title.default}
      onChange={(value) => onChangeFilterType(value as EquityEnumsSubscriptionPeriodEnum)}
      options={subscriptionRadioItemsSelect.items}
      selectedValue={selectedFilterType}
      limitDateRange={{
        future: new Date(),
      }}
    />
  );
};

export const SubscriptionFiltersModal = () => {
  const navigation = useNavigation();

  const goBack = useCallback(() => {
    if (navigation.canGoBack()) {
      navigation.goBack();
    }
  }, [navigation]);

  return (
    <FilterModal onClose={goBack} sliderModalWidth={1} filterAtom={subscriptionsFiltersAtom} />
  );
};
